import { useMemo } from 'react'
import { useIntl } from 'react-intl'

interface TourStyleBenefit {
  image: App.Image
  title: string
  description: string
}

function useTourStyleBenefits() {
  const intl = useIntl()

  return useMemo(() => {
    const tourStylesBenefits: Record<App.Tours.V2ProductTypeStyled, Array<TourStyleBenefit>> = {
      deluxe_tour: [
        {
          image: { id: 'ogli9t02bmf7bpd3y01m' },
          title: intl.formatMessage({
            id: 'D4U2ncPJ2y',
            defaultMessage: 'Unmissable experiences',
            description: 'Tour benefit title for deluxe tours',
          }),
          description: intl.formatMessage({
            id: '+Mf/oS2yFd',
            defaultMessage: 'The best of every destination: curated activities and must-see highlights',
            description: 'Tour benefit description for deluxe tours experiences',
          }),
        },
        {
          image: { id: 'vohjh1k4mqb93gjbmnhb' },
          title: intl.formatMessage({
            id: 'nbRLv4Y6Ug',
            defaultMessage: 'Premium accommodation',
            description: 'Tour benefit title for deluxe tours accommodation',
          }),
          description: intl.formatMessage({
            id: 'Pv5uN1EiMR',
            defaultMessage: 'Carefully selected 4- and 4.5-star hotels for comfort and convenience throughout',
            description: 'Tour benefit description for deluxe tours accommodation',
          }),
        },
      ],
      premium_tour: [
        {
          image: { id: 'pt7qf3vv7r2r1vbkper' },
          title: intl.formatMessage({
            id: 'Z5qZDMWEJV',
            defaultMessage: 'Dedicated agent',
            description: 'Tour benefit title for premium tours',
          }),
          description: intl.formatMessage({
            id: 'nPkEWHrn1/',
            defaultMessage: 'Personalised support with a dedicated Tours Concierge agent',
            description: 'Tour benefit description for premium tours dedicated agent',
          }),
        },
        {
          image: { id: '4c00aigy1blnhl4zy4g' },
          title: intl.formatMessage({
            id: 'aTgtmr38if',
            defaultMessage: 'Five-star accommodation',
            description: 'Tour benefit title for premium tours accommodation',
          }),
          description: intl.formatMessage({
            id: 'Hg+6QEjF9K',
            defaultMessage: 'Elevated five-star hotels to ensure enhanced comfort and quality',
            description: 'Tour benefit description for premium tours accommodation',
          }),
        },
      ],
      ultra_lux_tour: [
        {
          image: { id: 'inm4w1t2o6fg9nyac9i' },
          title: intl.formatMessage({
            id: 'QeZ9yNye4S',
            defaultMessage: 'World-class stays',
            description: 'Tour benefit title for ultra lux tours',
          }),
          description: intl.formatMessage({
            id: '4bGiy/01fe',
            defaultMessage: "The world's most prestigious hotels, featuring unparalleled five-star properties and global icons",
            description: 'Tour benefit description for ultra lux tours accommodation',
          }),
        },
      ],
      signature_series_tour: [
        {
          image: { id: 'jsv70zstdiovyv92u68a' },
          title: intl.formatMessage({
            id: '3eTb0PGgmB',
            defaultMessage: 'Celebrity hosted',
            description: 'Tour benefit title for signature series tours',
          }),
          description: intl.formatMessage({
            id: 'c7TAx8p4UT',
            defaultMessage: "Ultra-exclusive journeys are designed and led by some of Australia's most-loved personalities",
            description: 'Tour benefit description for signature series celebrity hosting',
          }),
        },
        {
          image: { id: '3dlvhljdlpmxfjipck2p' },
          title: intl.formatMessage({
            id: 'BGF7cKcX5D',
            defaultMessage: 'World-class stays',
            description: 'Tour benefit title for signature series tours accommodation',
          }),
          description: intl.formatMessage({
            id: 'ohzJhoXgfq',
            defaultMessage: "The world's most prestigious hotels, featuring unparalleled five-star properties and global icons",
            description: 'Tour benefit description for signature series tours accommodation',
          }),
        },
      ],
      custom_tour: [
        {
          image: { id: 'pt7qf3vv7r2r1vbkper' },
          title: intl.formatMessage({
            id: 'uEupFPGbYq',
            defaultMessage: 'Effortless Travel',
            description: 'Tour benefit title for custom tours travel',
          }),
          description: intl.formatMessage({
            id: 'JFqYtCRvqJ',
            defaultMessage: 'Skip airport chaos with fast-tracked service at dedicated gates for stress-free travel between places not easily accessible via standard commercial routes',
            description: 'Tour benefit description for custom tours travel',
          }),
        },
        {
          image: { id: '3dlvhljdlpmxfjipck2p' },
          title: intl.formatMessage({
            id: '7BbChUqepl',
            defaultMessage: 'Handpicked, Luxury Stays',
            description: 'Tour benefit title for custom tours accommodation',
          }),
          description: intl.formatMessage({
            id: 'xMj099mj7C',
            defaultMessage: "Experience Europe at its finest with stays in the continent's top 4- and 5-star hotels, with the option to elevate your journey even further with suite upgrades",
            description: 'Tour benefit description for custom tours accommodation',
          }),
        },
      ],
      insider_series_tour: [
        {
          image: { id: 'tourinsideritinerarycvpimage' },
          title: intl.formatMessage({
            id: '9SJHRXnUf6',
            defaultMessage: 'Insider-Curated Itinerary',
            description: 'Tour benefit title for insider series tours itinerary',
          }),
          description: intl.formatMessage({
            id: 'qiTXqSaY1t',
            defaultMessage: "Explore a destination through an expert's eyes, with curated experiences & hidden gems they love most.",
            description: 'Tour benefit description for insider series tours itinerary',
          }),
        },
        {
          image: { id: 'tourexpertlycuratedcvpimage' },
          title: intl.formatMessage({
            id: 'rQ3iUG3IHb',
            defaultMessage: 'Premium Accommodation',
            description: 'Tour benefit title for insider series tours accommodation',
          }),
          description: intl.formatMessage({
            id: 'bUlLaeNefg',
            defaultMessage: 'Unwind in elevated hotels designed for enhanced comfort and quality.',
            description: 'Tour benefit description for insider series tours accommodation',
          }),
        },
      ],
    }

    const tourStylesGroupBenefit: Record<App.Tours.V2ProductTypeStyled, TourStyleBenefit> = {
      deluxe_tour: {
        image: { id: 'iko1j3uoook3lxd3wn0r' },
        title: intl.formatMessage({
          id: '6mzxlhTGwQ',
          defaultMessage: 'Max 24 travellers',
          description: 'Tour benefit title for deluxe tours group size',
        }),
        description: intl.formatMessage({
          id: 'fKKfGPdaH6',
          defaultMessage: 'Small-group journeys with a maximum of 24 travellers',
          description: 'Tour benefit description for deluxe tours group size',
        }),
      },
      premium_tour: {
        image: { id: 'iko1j3uoook3lxd3wn0r' },
        title: intl.formatMessage({
          id: '+04fjJ2bdI',
          defaultMessage: 'Max 16 travellers',
          description: 'Tour benefit title for premium tours group size',
        }),
        description: intl.formatMessage({
          id: '/p5Mr1VGYt',
          defaultMessage: 'A more personal and immersive experience at every destination with a maximum of 16 travellers',
          description: 'Tour benefit description for premium tours group size',
        }),
      },
      ultra_lux_tour: {
        image: { id: 'iko1j3uoook3lxd3wn0r' },
        title: intl.formatMessage({
          id: 'jYewXVpL59',
          defaultMessage: 'Max 12 travellers',
          description: 'Tour benefit title for ultra lux tours group size',
        }),
        description: intl.formatMessage({
          id: 'sWT/dEluvV',
          defaultMessage: 'An intimate, exclusive journey with no more than 12 travellers, ensuring a personal and tailored experience',
          description: 'Tour benefit description for ultra lux tours group size',
        }),
      },
      signature_series_tour: {
        image: { id: 'io15a8zwv4o5agoksr' },
        title: intl.formatMessage({
          defaultMessage: 'One-off departure',
          id: 'm2gautcvPc',
          description: 'Tour benefit title for signature series tours departure',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Just one departure date, ensuring that every trip is a unique, once-in-a-lifetime experience',
          id: 'G13c2ss0kr',
          description: 'Tour benefit description for signature series tours departure',
        }),
      },
      custom_tour: {
        image: { id: 'iko1j3uoook3lxd3wn0r' },
        title: intl.formatMessage({
          id: 'V/33TcMv4N',
          defaultMessage: 'Flexibility, Designed to Fit You',
          description: 'Tour benefit title for custom tours flexibility',
        }),
        description: intl.formatMessage({
          id: 'HaxlShKzfQ',
          defaultMessage: 'Enhance your journey with a selection of curated experiences or enjoy the freedom to explore at your own pace with ample time to discover each destination your way',
          description: 'Tour benefit description for custom tours flexibility',
        }),
      },
      insider_series_tour: {
        image: { id: 'toursmallgroupjourneycvpimage' },
        title: intl.formatMessage({
          id: 'zWkCTm8MGh',
          defaultMessage: 'Small-Group Journeys',
          description: 'Tour benefit title for insider series tours group size',
        }),
        description: intl.formatMessage({
          id: 'M+YE73Dr0t',
          defaultMessage: 'Travel in a small group for easier access to each destination.',
          description: 'Tour benefit description for insider series tours group size',
        }),
      },
    }

    const privateTourBenefit: TourStyleBenefit = {
      image: { id: 't5ctbm7gdm14t24ofo4' },
      title: intl.formatMessage({
        id: '3VUwg8vpC1',
        defaultMessage: 'Private tour',
        description: 'Tour benefit title for private tours',
      }),
      description: intl.formatMessage({
        id: 'UtksijJ+Mc',
        defaultMessage: 'An intimate, exclusive journey with just you and your partner, ensuring a personal and tailored experience',
        description: 'Tour benefit description for private tours',
      }),
    }

    const tourStyledHeading: Record<App.Tours.V2ProductTypeStyled, string> = {
      deluxe_tour: intl.formatMessage({
        id: 'j0VklZPiFT',
        defaultMessage: 'Key Benefits of Deluxe Tours',
        description: 'Heading for deluxe tour key benefits section',
      }),
      premium_tour: intl.formatMessage({
        id: 'szDqThfT+X',
        defaultMessage: 'Key Benefits of Premium Tours',
        description: 'Heading for premium tour key benefits section',
      }),
      ultra_lux_tour: intl.formatMessage({
        id: 'ahNHQldybQ',
        defaultMessage: 'Key Benefits of Ultra Lux Tours',
        description: 'Heading for ultra lux tour key benefits section',
      }),
      signature_series_tour: intl.formatMessage({
        id: '9i9b2Kzlyu',
        defaultMessage: 'Key Benefits of Signature Series Tours',
        description: 'Heading for signature series tour key benefits section',
      }),
      custom_tour: intl.formatMessage({
        id: 'igvheJ/ZgN',
        defaultMessage: 'Key Benefits of your once-in-a-lifetime tour',
        description: 'Heading for custom tour key benefits section',
      }),
      insider_series_tour: intl.formatMessage({
        id: '7zRe5CI7bD',
        defaultMessage: 'Key Benefits of Insider Series Tours',
        description: 'Heading for insider series tour key benefits section',
      }),
    }

    const privateTourStyledHeading: Record<App.Tours.V2ProductTypeStyled, string> = {
      deluxe_tour: intl.formatMessage({
        id: 'GAthoRdVv9',
        defaultMessage: 'Key Benefits of Private Deluxe Tours',
        description: 'Heading for private deluxe tour key benefits section',
      }),
      premium_tour: intl.formatMessage({
        id: 'bPtJLgNUgA',
        defaultMessage: 'Key Benefits of Private Premium Tours',
        description: 'Heading for private premium tour key benefits section',
      }),
      ultra_lux_tour: intl.formatMessage({
        id: '7TjEFbrUDZ',
        defaultMessage: 'Key Benefits of Private Ultra Lux Tours',
        description: 'Heading for private ultra lux tour key benefits section',
      }),
      signature_series_tour: intl.formatMessage({
        id: 'tRzNdX3/tM',
        defaultMessage: 'Key Benefits of Private Signature Series Tours',
        description: 'Heading for private signature series tour key benefits section',
      }),
      custom_tour: intl.formatMessage({
        id: 'd7pcidD33r',
        defaultMessage: 'Key benefits of your private once-in-a-lifetime tour',
        description: 'Heading for private custom tour key benefits section',
      }),
      insider_series_tour: intl.formatMessage({
        id: 'lj1/HwxWGo',
        defaultMessage: 'Key benefits of your private Insider Series tour',
        description: 'Heading for private insider series tour key benefits section',
      }),
    }

    return { tourStylesBenefits, tourStylesGroupBenefit, privateTourBenefit, tourStyledHeading, privateTourStyledHeading }
  }, [intl])
}

export default useTourStyleBenefits
